/**
 * The boundary schema for wizard answers.
 *
 * WHY THE SERVER NEEDS THIS AT ALL. When someone signs up, the API takes the
 * answers they gave the calculator and **re-runs `determine()` over them**,
 * exactly as `saveResult` already does. A client that reported its own verdict
 * could report a favourable one; a server that recomputes cannot be lied to.
 * Recomputing means the server needs the same answers→facts mapping the browser
 * used, which is why `facts.ts` moved here rather than being copied.
 *
 * WHY THE ANSWERS ARE THE DURABLE THING, not the facts. `CaseFacts` is derived —
 * it folds the wizard's "not sure" into null, resolves the transitional block,
 * and drops anything the engine has no use for. The answers are what the person
 * actually said, and they are what has to be shown back when they return to
 * change one (F5). Store the answers; derive the facts on every read.
 *
 * ONE THING THIS SCHEMA MUST NEVER GAIN. `sponsor_receives_benefit` is a plain
 * boolean and stays one. Which disability or carer's benefit somebody receives
 * is health data — special category under UK GDPR Article 9 — and §9.3 rests the
 * short-form DPIA on this stage holding none. The chips exist so a person can
 * recognise their own benefit; the answer is reduced before it leaves the
 * question (ADR-0023). A field naming a benefit here would undo that quietly.
 */
import { z } from "zod";
import type { WizardAnswers } from "./facts.js";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ISO date expected");
/** "not sure" is a real answer meaning unknown, and must survive as null. */
const tribool = z.boolean().nullable();

export const wizardAnswersSchema = z.object({
  sponsor_status: z
    .enum([
      "british_or_irish",
      "settled",
      "protection_status",
      "eu_settlement_limited_leave",
      "ecaa_worker_or_business",
      "stateless_permission",
      "none_of_these",
    ])
    .nullable(),
  sponsor_receives_benefit: tribool,

  stage: z.enum(["EC", "LTR", "ILR", "NAT", "FIANCE"]),
  intended_date: isoDate.nullable(),
  first_applied_before_cutoff: tribool,
  granted_on_first_application: tribool,
  holds_five_year_route_permission: tribool,
  applying_with_same_partner: tribool,

  relationship_type: z.enum(["marriage", "civil_partnership", "unmarried_partner"]),
  date_of_marriage: isoDate.nullable(),
  marriage_legally_valid: tribool,
  cohabiting_since: isoDate.nullable(),
  both_over_18: tribool,
  met_in_person: tribool,
  within_prohibited_degrees: tribool,
  previous_relationships_ended: tribool,

  route: z.enum(["employment", "savings", "combination", "other"]),
  income_from: z.enum(["sponsor", "applicant", "both"]).nullable(),
  months_with_employer: z.number().min(0).max(1200).nullable(),
  gross_annual: z.number().min(0).max(10_000_000).nullable(),
  savings_amount: z.number().min(0).max(100_000_000).nullable(),
  savings_held_since: isoDate.nullable(),

  english_exemption: z.enum(["none", "nationality", "qualification", "age", "medical"]),
  english_level: z.enum(["none", "A1", "A2", "B1", "B2"]).nullable(),

  legal_right_to_occupy: tribool,
  without_public_funds: tribool,

  life_in_uk_passed: tribool,

  dependent_children: z.boolean(),
});

/**
 * The schema and the type must describe the same thing. TypeScript checks one
 * direction here at compile time; `schema.test.ts` walks a real answer set
 * through both. A field added to `WizardAnswers` and forgotten here would be
 * silently dropped at the boundary — the answers would round-trip missing a
 * question the person actually answered.
 */
export type ParsedWizardAnswers = z.infer<typeof wizardAnswersSchema>;
const _typeCheck: ParsedWizardAnswers extends WizardAnswers ? true : never = true;
void _typeCheck;

/**
 * Which shape of answers a stored record holds.
 *
 * Bumped whenever the questions change in a way an old record cannot satisfy.
 * Records keep the version they were written with, so a case saved last month
 * is readable today without a migration — the whole reason answers live in
 * JSONB and the spine does not.
 */
export const ANSWERS_SCHEMA_VERSION = 1;
